import { useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV_LINKS = [
  { label: "Services", href: "#services" },
  { label: "Why Us", href: "#why" },
  { label: "Gallery", href: "#gallery" },
  { label: "Reviews", href: "#reviews" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

export default function Header({ scrolled }) {
  const [open, setOpen] = useState(false);

  return (
    <header
      className={`sticky top-0 z-40 transition-all duration-500 ${
        scrolled
          ? "glass-nav border-b border-[#B08D5B]/20 shadow-[0_1px_30px_-10px_rgba(11,27,51,0.15)]"
          : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 lg:h-24">
          <a href="#top" className="flex items-center gap-3 group" data-testid="site-logo">
            <div className="relative">
              <div className="w-11 h-11 border border-[#B08D5B] flex items-center justify-center group-hover:bg-[#B08D5B] transition-colors duration-500">
                <span className="font-heading text-xl font-semibold text-[#B08D5B] group-hover:text-white transition-colors">
                  P
                </span>
              </div>
            </div>
            <div className="leading-none">
              <div className="font-heading text-[#0B1B33] text-xl tracking-tight font-medium">
                Prime<span className="italic font-normal text-[#B08D5B]">GTA</span>
                <span className="font-light"> Remodeling</span>
              </div>
              <div className="text-[9px] uppercase tracking-[0.32em] text-stone-500 mt-1.5 hidden sm:block font-body font-semibold">
                Architecture · Design · Build
              </div>
            </div>
          </a>

          <nav className="hidden lg:flex items-center gap-9">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="font-body text-[13px] font-medium text-stone-700 hover:text-[#B08D5B] transition-colors relative group"
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 right-0 h-px bg-[#B08D5B] scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
              </a>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-5">
            <a
              href="tel:+14168264590"
              className="flex items-center gap-2 text-sm font-medium text-stone-700 hover:text-[#B08D5B] transition-colors"
              data-testid="header-phone-link"
            >
              <Phone className="w-3.5 h-3.5" />
              +1 416 826 4590
            </a>
            <Button
              asChild
              className="bg-[#0B1B33] hover:bg-[#142847] text-white rounded-none px-6 h-11 font-medium tracking-wide text-[13px] transition-all duration-500 hover:shadow-[0_10px_30px_-10px_rgba(11,27,51,0.6)] border border-[#0B1B33] hover:border-[#B08D5B]"
              data-testid="header-cta-quote"
            >
              <a href="#contact">Get Free Quote</a>
            </Button>
          </div>

          <button
            className="lg:hidden p-2 text-stone-800"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {open && (
          <div
            className="lg:hidden pb-5 border-t border-[#B08D5B]/20 mt-2 pt-5"
            data-testid="mobile-nav"
          >
            <div className="flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="font-body text-base text-stone-800 py-1"
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </a>
              ))}
              <a
                href="tel:+14168264590"
                className="flex items-center gap-2 text-sm font-medium text-stone-800 py-1"
              >
                <Phone className="w-4 h-4" /> +1 416 826 4590
              </a>
              <Button
                asChild
                className="bg-[#0B1B33] hover:bg-[#142847] text-white rounded-none w-full"
                data-testid="mobile-cta-quote"
              >
                <a href="#contact" onClick={() => setOpen(false)}>
                  Get Free Quote
                </a>
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
